import { CATEGORIES, COMICS, type Comic } from "@/mocks/comics";

export function getComicById(id: string | undefined): Comic | undefined {
  if (!id) return undefined;
  return COMICS.find((item) => item.id === id);
}

export function getCategoryByKey(key: string | undefined) {
  if (!key) return undefined;
  return CATEGORIES.find((item) => item.key === key);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return `${views}`;
}

export function relatedComics(comic: Comic, limit = 6): Comic[] {
  return COMICS
    .filter((item) => item.id !== comic.id)
    .map((item) => ({
      item,
      weight: item.categories.filter((c) => comic.categories.includes(c)).length,
    }))
    .sort((a, b) => b.weight - a.weight || b.item.views - a.item.views)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export interface FilterOptions {
  status?: string;
  region?: string;
  year?: string;
  sort?: string;
}

export function filterComics(list: Comic[], options: FilterOptions): Comic[] {
  const result = list.filter((item) => {
    if (options.status && options.status !== "全部" && item.status !== options.status) return false;
    if (options.region && options.region !== "全部" && item.region !== options.region) return false;
    if (options.year && options.year !== "全部" && String(item.year) !== options.year) return false;
    return true;
  });

  switch (options.sort) {
    case "hot":
      return [...result].sort((a, b) => b.views - a.views);
    case "score":
      return [...result].sort((a, b) => b.score - a.score);
    case "latest":
    default:
      return [...result].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }
}

export function searchComics(keyword: string): Comic[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return COMICS.filter((item) => {
    const haystack = [
      item.title,
      item.author,
      item.tagline,
      item.intro,
      item.categories.join(" "),
      item.tags.join(" "),
      item.region,
      item.status,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(key);
  });
}

export function chaptersOf(comic: Comic): { index: number; title: string; date: string }[] {
  const count = Math.min(comic.chapters, 24);
  const items = [] as { index: number; title: string; date: string }[];
  for (let i = 0; i < count; i += 1) {
    const index = comic.chapters - i;
    const base = new Date(`${comic.updatedAt}T00:00:00`);
    base.setDate(base.getDate() - i * 4);
    items.push({
      index,
      title: `第 ${String(index).padStart(3, "0")} 话`,
      date: `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, "0")}-${String(base.getDate()).padStart(2, "0")}`,
    });
  }
  return items;
}

export function paginate<T>(list: T[], page: number, pageSize: number) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: list.slice(start, start + pageSize),
    totalPages,
    page: safePage,
  };
}